import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DashboardTable } from 'src/components/DashboardTable';
import { DashboardTableDrawer } from 'src/components/DashboardTable/styled';
import Loader from 'src/components/Loader';
import { ReactComponent as CopyIcon } from 'src/icons/copy.svg';
import { ReactComponent as ListAltIcon } from 'src/icons/listAlt.svg';
import { ReactComponent as ThumbDownIcon } from 'src/icons/thumbDownAlt.svg';
import { ReactComponent as ThumbUpIcon } from 'src/icons/thumbUpAlt.svg';
import palette from 'src/theme/palette';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';

import { SET_CONVERSATION_MESSAGE_REACTION } from '../../../utils/gql/mutations';
import {
  ConversationMessage,
  ConversationMessageRole,
  Mutation,
  SetConversationMessageReactionArgs,
} from '../../../utils/gql/types';
import { DashboardTableInitData } from '../../DashboardTable/types';
import {
  AnswerMessageWrapper,
  LetterAvatarWrapper,
  MarkdownBody,
  ClosingDecisionMemoMarkdownBody,
  StreamingMarkdownBody,
  MessageActionButton,
  MessagesContainer,
  MessageText,
  MessageWrapper,
  ResLetterAvatarWrapper,
  StreamingMessageText,
  TableButton,
} from '../styled';
import type { Components } from 'react-markdown';
import { GptForm, UpdateConversationContextPayload } from '../types';
import { ChatMarkdownImg } from './chatMarkdownImg';
import { createRemarkGfmMarkdownComponents } from './chatMarkdownTableComponents';
import { StreamProgressTimeline } from './StreamProgressTimeline';
import type { StreamProgressStep } from '../../../utils/conversationStreamEvents';
import {
  downloadFirstMarkdownTableAsCsv,
  extractFirstMarkdownTable,
} from '../utils/markdownTableToCsv';
import type { MarkdownTableCsvExportLabels } from '../utils/markdownTableCsvUiLabels';
import {
  getMarkdownTableCsvExportLabels,
  getPrecedingUserMessageText,
  shouldHideMarkdownTableCsvButton,
} from '../utils/markdownTableCsvUiLabels';
function splitAssistantMarkdownForClosingMemo(text: string): {
  reportMarkdown: string;
  memoMarkdown: string | null;
} {
  const marker = '## Closing Decision Memo';
  const idx = text.indexOf(marker);
  if (idx < 0) {
    return { reportMarkdown: text, memoMarkdown: null };
  }
  return {
    reportMarkdown: text.slice(0, idx).trimEnd(),
    memoMarkdown: text.slice(idx).trim(),
  };
}

function StatusChip({ value }: { value: string }) {
  const v = value.trim().toLowerCase();
  const map: Record<
    string,
    { label: string; color: 'success' | 'warning' | 'error' | 'info' }
  > = {
    keep: { label: 'Keep', color: 'success' },
    monitor: { label: 'Monitor', color: 'warning' },
    'cut / replace': { label: 'Cut / Replace', color: 'error' },
    cut: { label: 'Cut / Replace', color: 'error' },
    retest: { label: 'Retest', color: 'info' },
  };
  const hit =
    map[v] ||
    (v.includes('cut') ? map.cut : null) ||
    (v.includes('monitor') ? map.monitor : null) ||
    (v.includes('keep') ? map.keep : null) ||
    (v.includes('retest') ? map.retest : null);

  if (!hit) return <>{value}</>;
  return (
    <Chip
      size="small"
      color={hit.color}
      label={hit.label}
      sx={{ fontWeight: 700 }}
    />
  );
}

function downloadChatAttachment(att: {
  filename: string;
  mimeType: string;
  content: string;
}): void {
  const blob = new Blob([att.content], {
    type: att.mimeType?.includes('csv')
      ? 'text/csv;charset=utf-8'
      : att.mimeType,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = att.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface Props {
  loading: boolean;
  updateConversationContext(payload: UpdateConversationContextPayload): void;
  isFinalizingStream?: boolean;
  finalizingLabel?: string;
  streamProgressSteps?: StreamProgressStep[];
}

const remarkGfmMarkdownComponents: Components = {
  ...createRemarkGfmMarkdownComponents(StatusChip),
  img: (props) => (
    <ChatMarkdownImg
      {...(props as React.ImgHTMLAttributes<HTMLImageElement>)}
    />
  ),
};

export const Messages: React.FunctionComponent<Props> = ({
  loading,
  updateConversationContext,
  isFinalizingStream,
  finalizingLabel,
  streamProgressSteps = [],
}) => {
  const theme = useTheme();
  const { watch, getValues, setValue } = useFormContext<GptForm>();
  const snackbar = useSnackbar();

  const messages = watch('messages');

  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);
  const [tableTitle, setTableTitle] = useState<string>('');
  const [initDashboardTableData, setInitDashboardTableData] =
    useState<DashboardTableInitData>();

  const [setMessageReaction] = useMutation<
    Pick<Mutation, 'setConversationMessageReaction'>,
    SetConversationMessageReactionArgs
  >(SET_CONVERSATION_MESSAGE_REACTION);

  const handleMessageReaction = async (messageId: string, helpful: boolean) => {
    await setMessageReaction({
      variables: {
        messageId,
        helpful,
      },
    });

    const messages = getValues('messages');
    const updatedMessages = messages.map((m) => {
      if (m.id === messageId) {
        return {
          ...m,
          helpful,
        };
      }

      return m;
    });

    setValue('messages', updatedMessages);
  };

  const handleCopyMessage = async (messageText: string) => {
    try {
      await navigator.clipboard.writeText(messageText);
      snackbar('Message copied to clipboard!', 'success');
    } catch (error) {
      console.error('Errore durante la copia:', error);
      snackbar('Error copying message', 'error');
    }
  };

  const handleExportMarkdownTableCsv = (
    message: ConversationMessage,
    labels: MarkdownTableCsvExportLabels,
  ) => {
    const ok = downloadFirstMarkdownTableAsCsv(
      message.text ?? '',
      `${labels.filenamePrefix}-${message.id}`,
    );
    if (ok) {
      snackbar(labels.snackbarSuccess, 'success');
    } else {
      snackbar('No markdown table found in this message', 'info');
    }
  };

  const handeTableButtonClick = (message: ConversationMessage) => {
    // Tronca il titolo per evitare che copra le date
    const truncatedTitle =
      message.text.length > 50
        ? message.text.substring(0, 50) + '...'
        : message.text;
    setTableTitle(truncatedTitle);
    setIsModalOpened(true);

    if (message.table) {
      // Normalizza subito rows per includere sempre database (anche se assente) e passare i dati alla tabella
      const normalizedRows = {
        facebook: message.table.rows?.facebook || null,
        googleAds: message.table.rows?.googleAds || null,
        shopify: message.table.rows?.shopify || null,
        googleAnalytics4: message.table.rows?.googleAnalytics4 || null,
        customFormula: message.table.rows?.customFormula || null,
        database:
          message.table.rows?.database ||
          // fallback: se il dataset ha righe database ma non sono nel table, prova a prenderle dall'ultimo messaggio precedente
          (messages
            .slice(0)
            .reverse()
            .find((m) => m.table?.rows?.database)?.table?.rows
            ?.database as any) ||
          null,
      } as any;

      setInitDashboardTableData({
        ...message.table,
        rows: normalizedRows,
        dateRange: message.table.dateRange,
      });
    }
  };

  const handleTableClose = (payload: UpdateConversationContextPayload) => {
    setIsModalOpened(false);
    updateConversationContext(payload);
  };

  const hasStreamingMessage = messages.some((m) => m.id === 'streaming');
  const showPendingAssistant = loading && !hasStreamingMessage;

  return (
    <MessagesContainer>
      <Box
        display="flex"
        gap="10px"
        flexDirection="column"
        sx={{ width: '100%', minWidth: 0 }}
      >
        {messages.map((message, messageIndex) => {
          const precedingUserText = getPrecedingUserMessageText(
            messages,
            messageIndex,
          );
          const markdownCsvLabels =
            message.role === ConversationMessageRole.Assistant &&
            message.id !== 'streaming' &&
            !shouldHideMarkdownTableCsvButton(
              precedingUserText,
              message.attachments,
            )
              ? getMarkdownTableCsvExportLabels(precedingUserText)
              : null;

          const assistantMarkdownText =
            (message.text ?? '').trim() || 'Nessun contenuto da mostrare.';
          const { reportMarkdown, memoMarkdown } =
            message.role === ConversationMessageRole.Assistant &&
            message.id !== 'streaming'
              ? splitAssistantMarkdownForClosingMemo(assistantMarkdownText)
              : {
                  reportMarkdown: assistantMarkdownText,
                  memoMarkdown: null as string | null,
                };

          return (
            <Box
              key={message.id}
              display="flex"
              gap="10px"
              flexDirection="column"
              fontSize="18px"
              sx={{ width: '100%', minWidth: 0 }}
            >
              {message.role === ConversationMessageRole.User ? (
                <MessageWrapper>
                  <Box display="flex" height="100%" mb="auto">
                    <LetterAvatarWrapper>
                      <Typography
                        fontWeight={600}
                        fontSize="18px"
                        lineHeight="24px"
                        color={palette.primary.light}
                      >
                        U
                      </Typography>
                    </LetterAvatarWrapper>
                  </Box>
                  <Box
                    borderRadius="6px"
                    sx={{ minWidth: 0, maxWidth: '100%' }}
                  >
                    <MarkdownBody>
                      <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={remarkGfmMarkdownComponents}
                      >
                        {message.text}
                      </Markdown>
                    </MarkdownBody>
                  </Box>
                </MessageWrapper>
              ) : (
                <AnswerMessageWrapper>
                  <Box display="flex" height="100%" mb="auto">
                    <ResLetterAvatarWrapper>
                      <Typography
                        fontWeight={600}
                        fontSize="18px"
                        lineHeight="24px"
                      >
                        K
                      </Typography>
                    </ResLetterAvatarWrapper>
                  </Box>
                  <Box
                    display="flex"
                    gap="15px"
                    flexDirection="column"
                    sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}
                  >
                    <Box
                      borderRadius="6px"
                      display="flex"
                      flexDirection="column"
                      gap="10px"
                      sx={{ minWidth: 0, maxWidth: '100%' }}
                    >
                      {message.id === 'streaming' ? (
                        <>
                          {streamProgressSteps.length > 0 && (
                            <StreamProgressTimeline
                              steps={streamProgressSteps}
                            />
                          )}
                          {message.text ? (
                            <StreamingMarkdownBody>
                              <Markdown
                                remarkPlugins={[remarkGfm]}
                                components={remarkGfmMarkdownComponents}
                              >
                                {message.text}
                              </Markdown>
                            </StreamingMarkdownBody>
                          ) : streamProgressSteps.length === 0 ? (
                            <StreamingMessageText>…</StreamingMessageText>
                          ) : null}
                          {isFinalizingStream && (
                            <Box display="flex" gap="8px" alignItems="center">
                              <Loader size={14} />
                              <Typography
                                fontSize="12px"
                                color={palette.grey[600]}
                              >
                                {finalizingLabel ||
                                  'Analizzo e convalido la risposta…'}
                              </Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                        <>
                          {reportMarkdown ? (
                            <MarkdownBody>
                              <Markdown
                                remarkPlugins={[remarkGfm]}
                                components={remarkGfmMarkdownComponents}
                              >
                                {reportMarkdown}
                              </Markdown>
                            </MarkdownBody>
                          ) : null}
                          {memoMarkdown ? (
                            <ClosingDecisionMemoMarkdownBody>
                              <Markdown
                                remarkPlugins={[remarkGfm]}
                                components={remarkGfmMarkdownComponents}
                              >
                                {memoMarkdown}
                              </Markdown>
                            </ClosingDecisionMemoMarkdownBody>
                          ) : null}
                          {message.table && (
                            <TableButton
                              onClick={() => handeTableButtonClick(message)}
                              startIcon={<ListAltIcon />}
                            >
                              Open as Custom report
                            </TableButton>
                          )}
                          {extractFirstMarkdownTable(message.text ?? '') &&
                          markdownCsvLabels ? (
                            <TableButton
                              onClick={() =>
                                handleExportMarkdownTableCsv(
                                  message,
                                  markdownCsvLabels,
                                )
                              }
                              startIcon={<ListAltIcon />}
                            >
                              {markdownCsvLabels.buttonLabel}
                            </TableButton>
                          ) : null}
                          {message.attachments?.map((att, idx) =>
                            att.kind === 'csv' ? (
                              <TableButton
                                key={`${att.filename}-${idx}`}
                                onClick={() => downloadChatAttachment(att)}
                                startIcon={<ListAltIcon />}
                              >
                                Download {att.filename}
                              </TableButton>
                            ) : null,
                          )}
                        </>
                      )}
                    </Box>
                    {message.id !== 'streaming' && (
                      <Box display="flex" gap="15px">
                        <MessageActionButton
                          disabled={message.helpful}
                          onClick={() =>
                            handleMessageReaction(message.id, true)
                          }
                        >
                          <ThumbUpIcon
                            color={
                              message.helpful
                                ? theme.palette.secondary.light
                                : undefined
                            }
                          />
                        </MessageActionButton>
                        <MessageActionButton
                          disabled={message.helpful === false}
                          onClick={() =>
                            handleMessageReaction(message.id, false)
                          }
                        >
                          <ThumbDownIcon
                            color={
                              message.helpful === false
                                ? theme.palette.secondary.light
                                : undefined
                            }
                          />
                        </MessageActionButton>
                        <MessageActionButton
                          onClick={() => handleCopyMessage(message.text)}
                        >
                          <CopyIcon />
                        </MessageActionButton>
                      </Box>
                    )}
                  </Box>
                </AnswerMessageWrapper>
              )}
            </Box>
          );
        })}
        {showPendingAssistant ? (
          <AnswerMessageWrapper>
            <Box display="flex" height="100%" mb="auto">
              <ResLetterAvatarWrapper>
                <Typography fontWeight={600} fontSize="18px" lineHeight="24px">
                  K
                </Typography>
              </ResLetterAvatarWrapper>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap="10px"
              sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}
            >
              {streamProgressSteps.length > 0 ? (
                <StreamProgressTimeline steps={streamProgressSteps} />
              ) : (
                <Box display="flex" gap="15px" alignItems="center">
                  <Loader size={30} />
                  <MessageText color={palette.grey[800]}>
                    Stirring up a data cocktail...
                  </MessageText>
                </Box>
              )}
            </Box>
          </AnswerMessageWrapper>
        ) : null}
      </Box>

      <DashboardTableDrawer
        open={isModalOpened}
        anchor="bottom"
        onClose={() => setIsModalOpened(false)}
        disableRestoreFocus
        closeAfterTransition
        disableEscapeKeyDown
      >
        {isModalOpened && (
          <DashboardTable
            onCloseTable={handleTableClose}
            title={tableTitle}
            initData={initDashboardTableData}
            metricsIsShown={false}
            rows={[]}
            columnOrder={[]}
            changeColumnOrder={() => {}}
          />
        )}
      </DashboardTableDrawer>
    </MessagesContainer>
  );
};
